/**
 * Pure codec selection: probes encoder configs in preference order through an injected
 * `isConfigSupported` so it can be tested without WebCodecs.
 */

export type HardwareAcceleration = 'prefer-hardware' | 'prefer-software' | 'no-preference';

export interface VideoCodecRequest {
  codec?: 'h264' | 'av1';
  width: number;
  height: number;
  fps: number;
  videoKbps: number;
  hardwareAcceleration: HardwareAcceleration;
}

export interface AudioCodecRequest {
  sampleRate: number;
  numberOfChannels: number;
  audioKbps: number;
}

export type ConfigProbe<C> = (config: C) => Promise<{ supported?: boolean; config?: C }>;

export interface SelectedVideo {
  config: VideoEncoderConfig;
  profile: 'high' | 'main' | 'baseline' | 'av1-main';
}

export interface SelectedAudio {
  config: AudioEncoderConfig;
  muxCodec: 'aac' | 'opus';
}

/** H.264 level_idc (hex) large enough for the frame size and rate. */
export function h264LevelHex(width: number, height: number, fps: number): string {
  const macroblocksPerSecond = Math.ceil(width / 16) * Math.ceil(height / 16) * fps;
  const frameMacroblocks = Math.ceil(width / 16) * Math.ceil(height / 16);
  // (level, max MB/s, max frame size in MBs) from ITU-T H.264 Table A-1.
  const levels: Array<[string, number, number]> = [
    ['1f', 108_000, 3_600], // 3.1
    ['20', 216_000, 5_120], // 3.2
    ['28', 245_760, 8_192], // 4.0
    ['2a', 522_240, 8_704], // 4.2
    ['32', 589_824, 22_080], // 5.0
    ['33', 983_040, 36_864], // 5.1
  ];
  for (const [hex, mbps, fs] of levels) {
    if (macroblocksPerSecond <= mbps && frameMacroblocks <= fs) return hex;
  }
  return '34'; // 5.2
}

const H264_PROFILES: Array<{ profile: SelectedVideo['profile']; prefix: string }> = [
  { profile: 'high', prefix: '6400' },
  { profile: 'main', prefix: '4d40' },
  { profile: 'baseline', prefix: '42e0' }, // constrained baseline
];

export function h264Candidates(req: VideoCodecRequest): SelectedVideo[] {
  const level = h264LevelHex(req.width, req.height, req.fps);
  return H264_PROFILES.map(({ profile, prefix }) => ({
    profile,
    config: {
      codec: `avc1.${prefix}${level}`,
      width: req.width,
      height: req.height,
      framerate: req.fps,
      bitrate: req.videoKbps * 1000,
      bitrateMode: 'constant',
      latencyMode: 'realtime',
      hardwareAcceleration: req.hardwareAcceleration,
      avc: { format: 'avc' },
    },
  }));
}

/** Video of an H.264 `compat` rendition next to main. */
export interface CompatVideo { width: number; height: number; fps: number; videoKbps: number }

/** Bitrates per compat tier at 30 fps (H.264 High): 720p30 ≈ 2.5 Mbit/s, 480p30 ≈ 1.2 Mbit/s. */
const COMPAT_TIERS = { 720: { longSide: 1280, kbps: 2500 }, 480: { longSide: 854, kbps: 1200 } } as const;

/**
 * Size, rate and bitrate of the compat lane for a main encode: the main aspect ratio with its short side at most
 * `tier` (and its long side at most 1280 or 854), never larger than main, even dimensions, at most 30 fps, and at
 * most main's bitrate. The backend refuses anything above 720p, 30 fps or 4 Mbit/s.
 */
export function compatVideo(main: CompatVideo, tier: 720 | 480 = 720): CompatVideo {
  const { longSide, kbps } = COMPAT_TIERS[tier];
  const landscape = main.width >= main.height;
  const long = landscape ? main.width : main.height, short = landscape ? main.height : main.width;
  const scale = Math.min(1, tier / short, longSide / long);
  const even = (n: number) => Math.max(2, 2 * Math.round(n / 2));
  const w = even(main.width * scale), h = even(main.height * scale);
  const fps = Math.min(30, main.fps);
  return { width: Math.min(w, main.width - (main.width % 2)), height: Math.min(h, main.height - (main.height % 2)), fps,
    videoKbps: Math.min(main.videoKbps, Math.round(kbps * fps / 30)) };
}

export function audioCandidates(req: AudioCodecRequest): SelectedAudio[] {
  const base = { sampleRate: req.sampleRate, numberOfChannels: req.numberOfChannels, bitrate: req.audioKbps * 1000 };
  return [
    { muxCodec: 'aac', config: { codec: 'mp4a.40.2', ...base } },
    { muxCodec: 'opus', config: { codec: 'opus', ...base } },
  ];
}

async function firstSupported<T extends { config: C }, C>(candidates: T[], probe: ConfigProbe<C>): Promise<T | null> {
  for (const c of candidates) {
    try {
      const r = await probe(c.config);
      if (r.supported) return c;
    } catch {
      // A malformed or unknown config throws in some browsers: treat as unsupported.
    }
  }
  return null;
}

export async function selectVideoCodec(req: VideoCodecRequest, probe: ConfigProbe<VideoEncoderConfig>): Promise<SelectedVideo | null> {
  if (req.codec === 'av1') return firstSupported([{
    profile: 'av1-main' as const,
    config: {
      codec: 'av01.0.13M.08', width: req.width, height: req.height, framerate: req.fps,
      bitrate: req.videoKbps * 1000, bitrateMode: 'constant' as const,
      latencyMode: 'realtime' as const, hardwareAcceleration: req.hardwareAcceleration,
    },
  }], probe);
  return firstSupported(h264Candidates(req), probe);
}

export async function selectAudioCodec(req: AudioCodecRequest, probe: ConfigProbe<AudioEncoderConfig>): Promise<SelectedAudio | null> {
  return firstSupported(audioCandidates(req), probe);
}

/** RFC 6381 codec string from an avcC record (the encoder's actual profile/level, not the requested one). */
export function avcCodecStringFromAvcC(avcC: Uint8Array): string | null {
  if (avcC.length < 4 || avcC[0] !== 1) return null;
  const hex = (n: number) => n.toString(16).padStart(2, '0');
  return `avc1.${hex(avcC[1]!)}${hex(avcC[2]!)}${hex(avcC[3]!)}`;
}
